import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { money, apiErr } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Users, Plus, Search, FileText, Download, Trash2, UserPlus, Building2, Phone, Mail, MapPin, History, Hash, Wallet, Ban } from "lucide-react";
import { InvoiceSettingsDialog } from "@/components/InvoiceSettingsDialog";
import { toast } from "sonner";

const blank = { name: "", company: "", contact_person: "", designation: "", phone: "", email: "", address: "", city: "", state: "", gst_number: "", pan_number: "" };
const fmtDate = (iso) => (iso ? new Date(iso).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" }) : "—");

const StatusPill = ({ st }) => {
  const map = { paid: "bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400", partial: "bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400", unpaid: "bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400", cancelled: "bg-muted text-muted-foreground line-through" };
  return <span className={`inline-flex px-2 py-0.5 text-[10px] font-bold uppercase ${map[st] || "bg-muted text-muted-foreground"}`}>{st === "cancelled" ? "Cancelled" : st}</span>;
};

export default function CustomersPage() {
  const [customers, setCustomers] = useState([]);
  const [suggestions, setSuggestions] = useState([]);
  const [query, setQuery] = useState("");
  const [loading, setLoading] = useState(true);
  const [editFor, setEditFor] = useState(null); // customer obj or {} for new
  const [form, setForm] = useState(blank);
  const [profileFor, setProfileFor] = useState(null);
  const [invoices, setInvoices] = useState([]);
  const [allInvoices, setAllInvoices] = useState([]);

  const loadAll = () => api.get("/invoices").then((r) => setAllInvoices(r.data)).catch(() => {});
  const [numOpen, setNumOpen] = useState(false);
  const [setOpen, setSetOpen] = useState(false);
  const [numInfo, setNumInfo] = useState(null);
  const [numStart, setNumStart] = useState("");
  const [payFor, setPayFor] = useState(null);
  const [payAmount, setPayAmount] = useState("");
  const [payMode, setPayMode] = useState("Bank Transfer");
  const [payDate, setPayDate] = useState(new Date().toISOString().slice(0, 10));

  const load = () => api.get("/customers").then((r) => setCustomers(r.data)).catch((e) => toast.error(apiErr(e))).finally(() => setLoading(false));
  const loadSug = () => api.get("/customers/suggestions").then((r) => setSuggestions(r.data)).catch(() => {});
  useEffect(() => { load(); loadSug(); loadAll(); }, []);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return customers;
    return customers.filter((c) => [c.name, c.company, c.phone, c.gst_number].some((x) => (x || "").toLowerCase().includes(q)));
  }, [customers, query]);

  const openNew = (prefill) => { setForm(prefill ? { ...blank, ...prefill } : blank); setEditFor(prefill ? {} : {}); };
  const openEdit = (c) => { setForm({ ...blank, ...c }); setEditFor(c); };

  const saveCustomer = async () => {
    if (!form.name.trim()) return toast.error("Name is required");
    const isNew = !(editFor && editFor.id);
    if (isNew && form.phone.trim()) {
      try {
        const { data } = await api.post("/phone-check", { phones: [form.phone] });
        const hit = data[form.phone.replace(/\D/g, "")];
        if (hit && !window.confirm(`⚠ A record with this phone already exists (${hit.sample}). Add this customer anyway?`)) return;
      } catch (_) { /* non-blocking */ }
    }
    try {
      const body = { ...form };
      if (editFor && editFor.id) { await api.put(`/customers/${editFor.id}`, body); toast.success("Customer updated"); }
      else { await api.post("/customers", body); toast.success("Customer added"); }
      setEditFor(null); load(); loadSug();
    } catch (e) { toast.error(apiErr(e)); }
  };

  const delCustomer = async (c) => {
    if (!window.confirm(`Delete customer ${c.name}?`)) return;
    try { await api.delete(`/customers/${c.id}`); toast.success("Deleted"); load(); loadSug(); } catch (e) { toast.error(apiErr(e)); }
  };

  const openProfile = async (c) => {
    setProfileFor(c);
    setInvoices([]);
    try { const { data } = await api.get(`/invoices?customer_id=${c.id}`); setInvoices(data); } catch (e) { toast.error(apiErr(e)); }
  };

  const downloadInvoice = async (inv) => {
    try {
      const res = await api.get(`/invoices/${inv.id}/pdf`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a"); a.href = url; a.download = `${inv.serial.replace(/\//g, "-")}-invoice.pdf`; a.click();
      URL.revokeObjectURL(url);
    } catch (e) { toast.error(apiErr(e)); }
  };

  const navigate = useNavigate();
  const startInvoice = (customerId = "") => navigate(customerId ? `/dashboard/invoices/new?customer=${customerId}` : "/dashboard/invoices/new");
  const openEditInvoice = (inv) => navigate(`/dashboard/invoices/${inv.id}`);
  const voidInvoice = async (inv) => {
    const reason = window.prompt(`Cancel invoice ${inv.serial}? The number stays reserved and it can't be edited or paid afterward.\n\nEnter the cancellation reason:`);
    if (reason === null) return;
    if (!reason.trim()) return toast.error("A cancellation reason is required");
    try { await api.post(`/invoices/${inv.id}/void`, { reason: reason.trim() }); toast.success(`${inv.serial} cancelled`); loadAll(); if (profileFor) openProfile(profileFor); }
    catch (e) { toast.error(apiErr(e)); }
  };
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const openNumbering = async () => {
    setNumOpen(true); setNumInfo(null); setNumStart("");
    try { const { data } = await api.get("/invoices/numbering"); setNumInfo(data); setNumStart(String(data.next_number)); }
    catch (e) { toast.error(apiErr(e)); }
  };
  const saveNumbering = async () => {
    const n = parseInt(numStart, 10);
    if (isNaN(n) || n < 1) return toast.error("Enter a valid start number");
    try { const { data } = await api.post("/invoices/numbering", { fy: numInfo?.fy, start_number: n }); toast.success(`Next invoice for FY ${data.fy} will be #${String(data.next_number).padStart(4, "0")}`); setNumOpen(false); }
    catch (e) { toast.error(apiErr(e)); }
  };

  const openPay = (inv) => { setPayFor(inv); setPayAmount(""); setPayMode("Bank Transfer"); setPayDate(new Date().toISOString().slice(0, 10)); };
  const submitPay = async () => {
    const amt = parseFloat(payAmount);
    if (isNaN(amt) || amt <= 0) return toast.error("Enter a valid amount");
    try {
      await api.post(`/invoices/${payFor.id}/payments`, { amount: amt, mode: payMode, date: payDate });
      toast.success(`Payment of ${money(amt)} recorded`);
      setPayFor(null);
      if (profileFor) openProfile(profileFor);
      loadAll();
    } catch (e) { toast.error(apiErr(e)); }
  };

  return (
    <div>
      <div className="mb-6 flex flex-wrap items-end justify-between gap-4">
        <div>
          <div className="text-xs font-bold uppercase tracking-widest text-primary flex items-center gap-2"><Users className="h-4 w-4" /> Directory</div>
          <h1 className="font-heading font-black text-2xl sm:text-4xl tracking-tight">Customers</h1>
          <p className="text-muted-foreground text-sm">Manage client profiles, GST details and invoice history.</p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <div className="relative flex-1 sm:w-64">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input className="rounded-none pl-9 h-11 bg-card" placeholder="Search name / company / GST…" value={query} onChange={(e) => setQuery(e.target.value)} data-testid="cust-search" />
          </div>
          <Button variant="outline" className="rounded-none h-11 gap-2" onClick={openNumbering} data-testid="cust-numbering"><Hash className="h-4 w-4" /> Invoice Numbering</Button>
          <Button variant="outline" className="rounded-none h-11 gap-2" onClick={() => setSetOpen(true)} data-testid="cust-invoice-settings"><FileText className="h-4 w-4" /> Invoice Settings</Button>
          <Button className="rounded-none h-11 gap-2 font-bold uppercase tracking-wide" onClick={() => openNew(null)} data-testid="cust-add"><Plus className="h-4 w-4" /> Add Customer</Button>
        </div>
      </div>

      <div className="grid lg:grid-cols-[300px_1fr] gap-6 items-start">
        <div className="border border-border bg-card lg:sticky lg:top-4" data-testid="cust-doc-log">
          <div className="px-3 py-2.5 border-b border-border text-xs font-bold uppercase tracking-wide text-muted-foreground flex items-center gap-2"><FileText className="h-4 w-4 text-primary" /> Generated Documents ({allInvoices.length})</div>
          <div className="max-h-[70vh] overflow-y-auto divide-y divide-border">
            {allInvoices.length === 0 && <div className="p-4 text-xs text-muted-foreground text-center">No invoices or bills generated yet.</div>}
            {allInvoices.map((inv) => (
              <button key={inv.id} onClick={() => downloadInvoice(inv)} className="w-full text-left px-3 py-2 hover:bg-muted transition-colors" data-testid={`doc-log-${inv.id}`} title="Download PDF">
                <div className="flex items-center justify-between gap-2">
                  <span className="font-mono-num text-xs text-primary truncate">{inv.serial}</span>
                  <span className={`shrink-0 px-1.5 py-0.5 text-[9px] font-bold uppercase ${inv.invoice_type === "tax" ? "bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400" : "bg-slate-200 text-slate-700 dark:bg-slate-800 dark:text-slate-300"}`}>{inv.invoice_type === "tax" ? "Tax" : "BOG"}</span>
                </div>
                <div className="text-xs truncate">{inv.bill_to?.name || "—"}</div>
                <div className="flex items-center justify-between text-[11px] mt-0.5"><span className="font-mono-num text-muted-foreground">{money(inv.total)}</span><StatusPill st={inv.status} /></div>
              </button>
            ))}
          </div>
        </div>
        <div className="min-w-0 space-y-6">

      {suggestions.length > 0 && (
        <div className="mb-6 border border-border bg-card p-4" data-testid="cust-suggestions">
          <div className="text-xs font-bold uppercase tracking-wide text-muted-foreground mb-2 flex items-center gap-2"><UserPlus className="h-4 w-4 text-primary" /> Suggested from Leads ({suggestions.length})</div>
          <div className="flex gap-2 overflow-x-auto pb-1">
            {suggestions.slice(0, 30).map((s, i) => (
              <button key={i} onClick={() => openNew(s)} className="shrink-0 border border-border px-3 py-2 text-left hover:border-primary hover:bg-muted transition-colors" data-testid={`cust-sug-${i}`}>
                <div className="text-sm font-bold">{s.name || "—"}</div>
                <div className="text-[11px] text-muted-foreground">{s.company || s.phone}</div>
              </button>
            ))}
          </div>
        </div>
      )}

      <div className="border border-border bg-card">
        <div className="hidden md:grid grid-cols-[1.5fr_1.5fr_1fr_1fr_140px] gap-2 bg-muted px-4 py-2.5 text-[11px] font-bold uppercase tracking-wide text-muted-foreground">
          <span>Name</span><span>Company &amp; GST</span><span>Contact</span><span>Location</span><span className="text-right">Actions</span>
        </div>
        {loading && <div className="p-10 text-center text-muted-foreground">Loading…</div>}
        {!loading && filtered.length === 0 && <div className="p-10 text-center text-muted-foreground">No customers yet. Add one or import from suggestions.</div>}
        <div className="divide-y divide-border">
          {filtered.map((c) => (
            <div key={c.id} className="md:grid md:grid-cols-[1.5fr_1.5fr_1fr_1fr_140px] gap-2 px-4 py-3 items-center hover:bg-muted/40 transition-colors" data-testid={`cust-row-${c.id}`}>
              <button className="text-left" onClick={() => openProfile(c)} data-testid={`cust-open-${c.id}`}>
                <div className="font-bold text-sm text-primary hover:underline">{c.name}</div>
                {c.contact_person && <div className="text-[11px] text-muted-foreground">{c.contact_person}{c.designation ? ` · ${c.designation}` : ""}</div>}
              </button>
              <div className="text-sm"><div>{c.company || "—"}</div>{c.gst_number && <div className="text-[11px] font-mono-num text-muted-foreground">{c.gst_number}</div>}</div>
              <div className="text-xs"><div className="font-mono-num">{c.phone || "—"}</div>{c.email && <div className="text-muted-foreground truncate">{c.email}</div>}</div>
              <div className="text-xs text-muted-foreground">{[c.city, c.state].filter(Boolean).join(", ") || "—"}</div>
              <div className="flex md:justify-end gap-1 mt-2 md:mt-0">
                <Button size="sm" variant="outline" className="rounded-none text-xs gap-1" onClick={() => startInvoice(c.id)} data-testid={`cust-invoice-${c.id}`}><FileText className="h-3 w-3" /> Bill</Button>
                <Button size="sm" variant="ghost" className="rounded-none text-xs" onClick={() => openEdit(c)} data-testid={`cust-edit-${c.id}`}>Edit</Button>
                <button className="h-8 w-8 grid place-items-center text-muted-foreground hover:text-destructive" onClick={() => delCustomer(c)} data-testid={`cust-del-${c.id}`}><Trash2 className="h-4 w-4" /></button>
              </div>
            </div>
          ))}
        </div>
      </div>

        </div>
      </div>

      {/* Add/Edit dialog */}
      <Dialog open={!!editFor} onOpenChange={(o) => !o && setEditFor(null)}>
        <DialogContent className="rounded-none max-w-2xl max-h-[92vh] overflow-y-auto" data-testid="cust-form-dialog">
          <DialogHeader><DialogTitle className="font-heading uppercase tracking-tight">{editFor && editFor.id ? "Edit Customer" : "Add Customer"}</DialogTitle></DialogHeader>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 py-2">
            <div><Label className="text-xs">Name *</Label><Input className="rounded-none mt-1" value={form.name} onChange={(e) => set("name", e.target.value)} data-testid="cust-f-name" /></div>
            <div><Label className="text-xs">Company</Label><Input className="rounded-none mt-1" value={form.company} onChange={(e) => set("company", e.target.value)} data-testid="cust-f-company" /></div>
            <div><Label className="text-xs">Contact Person</Label><Input className="rounded-none mt-1" value={form.contact_person} onChange={(e) => set("contact_person", e.target.value)} /></div>
            <div><Label className="text-xs">Designation</Label><Input className="rounded-none mt-1" value={form.designation} onChange={(e) => set("designation", e.target.value)} /></div>
            <div><Label className="text-xs">Phone</Label><Input className="rounded-none mt-1 font-mono-num" value={form.phone} onChange={(e) => set("phone", e.target.value)} data-testid="cust-f-phone" /></div>
            <div><Label className="text-xs">Email</Label><Input className="rounded-none mt-1" value={form.email} onChange={(e) => set("email", e.target.value)} /></div>
            <div className="sm:col-span-2"><Label className="text-xs">Address</Label><Input className="rounded-none mt-1" value={form.address} onChange={(e) => set("address", e.target.value)} data-testid="cust-f-address" /></div>
            <div><Label className="text-xs">City</Label><Input className="rounded-none mt-1" value={form.city} onChange={(e) => set("city", e.target.value)} /></div>
            <div><Label className="text-xs">State</Label><Input className="rounded-none mt-1" value={form.state} onChange={(e) => set("state", e.target.value)} /></div>
            <div><Label className="text-xs">GST Number</Label><Input className="rounded-none mt-1 font-mono-num" value={form.gst_number} onChange={(e) => set("gst_number", e.target.value)} data-testid="cust-f-gst" /></div>
            <div><Label className="text-xs">PAN</Label><Input className="rounded-none mt-1 font-mono-num" value={form.pan_number} onChange={(e) => set("pan_number", e.target.value)} /></div>
          </div>
          <DialogFooter>
            <Button variant="ghost" className="rounded-none" onClick={() => setEditFor(null)}>Cancel</Button>
            <Button className="rounded-none font-bold uppercase tracking-wide" onClick={saveCustomer} data-testid="cust-save">Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Profile + invoice history */}
      <Dialog open={!!profileFor} onOpenChange={(o) => !o && setProfileFor(null)}>
        <DialogContent className="rounded-none max-w-2xl max-h-[92vh] overflow-y-auto" data-testid="cust-profile-dialog">
          <DialogHeader><DialogTitle className="font-heading uppercase tracking-tight">{profileFor?.name}</DialogTitle></DialogHeader>
          {profileFor && (
            <Tabs defaultValue="details">
              <TabsList className="rounded-none bg-muted">
                <TabsTrigger value="details" className="rounded-none gap-2" data-testid="cust-tab-details"><Building2 className="h-4 w-4" /> Details</TabsTrigger>
                <TabsTrigger value="invoices" className="rounded-none gap-2" data-testid="cust-tab-invoices"><History className="h-4 w-4" /> Invoice History</TabsTrigger>
              </TabsList>
              <TabsContent value="details" className="pt-4 space-y-2 text-sm">
                {profileFor.company && <div className="flex items-center gap-2"><Building2 className="h-4 w-4 text-muted-foreground" /> {profileFor.company}</div>}
                {profileFor.phone && <div className="flex items-center gap-2"><Phone className="h-4 w-4 text-muted-foreground" /> <span className="font-mono-num">{profileFor.phone}</span></div>}
                {profileFor.email && <div className="flex items-center gap-2"><Mail className="h-4 w-4 text-muted-foreground" /> {profileFor.email}</div>}
                {(profileFor.address || profileFor.city) && <div className="flex items-start gap-2"><MapPin className="h-4 w-4 text-muted-foreground mt-0.5" /> {[profileFor.address, profileFor.city, profileFor.state].filter(Boolean).join(", ")}</div>}
                {profileFor.gst_number && <div className="text-xs">GSTIN: <span className="font-mono-num">{profileFor.gst_number}</span></div>}
                {profileFor.pan_number && <div className="text-xs">PAN: <span className="font-mono-num">{profileFor.pan_number}</span></div>}
                <div className="pt-3"><Button className="rounded-none gap-2 font-bold uppercase tracking-wide" onClick={() => { setProfileFor(null); startInvoice(profileFor.id); }} data-testid="cust-profile-invoice"><FileText className="h-4 w-4" /> Create Invoice</Button></div>
              </TabsContent>
              <TabsContent value="invoices" className="pt-4">
                <div className="border border-border max-h-96 overflow-y-auto" data-testid="cust-invoice-history">
                  {invoices.length === 0 ? <div className="p-8 text-center text-sm text-muted-foreground">No invoices yet.</div> : (
                    <table className="w-full text-sm">
                      <thead><tr className="bg-muted text-left text-[11px] uppercase tracking-wide text-muted-foreground"><th className="px-3 py-2">Invoice</th><th className="px-3">Date</th><th className="px-3 text-right">Total</th><th className="px-3 text-right">Paid</th><th className="px-3 text-right">Pending</th><th className="px-3">Status</th><th className="px-3 text-right">Actions</th></tr></thead>
                      <tbody>
                        {invoices.map((inv) => (
                          <tr key={inv.id} className="border-t border-border" data-testid={`cust-inv-${inv.id}`}>
                            <td className="px-3 py-2 font-mono-num text-xs text-primary">{inv.serial}<div className="text-[10px] text-muted-foreground uppercase">{inv.invoice_type === "tax" ? "Tax Invoice" : "Bill of Supply"}</div></td>
                            <td className="px-3 text-xs">{fmtDate(inv.invoice_date || inv.created_at)}</td>
                            <td className="px-3 text-right font-mono-num">{money(inv.total)}</td>
                            <td className="px-3 text-right font-mono-num text-emerald-600 dark:text-emerald-400">{money(inv.paid)}</td>
                            <td className="px-3 text-right font-mono-num font-bold">{money((inv.total || 0) - (inv.paid || 0))}</td>
                            <td className="px-3"><StatusPill st={inv.status} /></td>
                            <td className="px-3 text-right whitespace-nowrap">
                              {inv.status !== "paid" && inv.status !== "cancelled" && <button onClick={() => { setProfileFor(null); openEditInvoice(inv); }} className="text-foreground hover:underline inline-flex items-center gap-1 text-xs mr-2" data-testid={`cust-inv-edit-${inv.id}`}><FileText className="h-3 w-3" /> Edit</button>}
                              {inv.status !== "paid" && inv.status !== "cancelled" && <button onClick={() => openPay(inv)} className="text-emerald-600 dark:text-emerald-400 hover:underline inline-flex items-center gap-1 text-xs mr-2" data-testid={`cust-inv-pay-${inv.id}`}><Wallet className="h-3 w-3" /> Record Payment</button>}
                              {inv.status !== "cancelled" && <button onClick={() => voidInvoice(inv)} className="text-red-500 hover:underline inline-flex items-center gap-1 text-xs mr-2" data-testid={`cust-inv-void-${inv.id}`}><Ban className="h-3 w-3" /> Void</button>}
                              <button onClick={() => downloadInvoice(inv)} className="text-primary hover:underline inline-flex items-center gap-1 text-xs" data-testid={`cust-inv-dl-${inv.id}`}><Download className="h-3 w-3" /> PDF</button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  )}
                </div>
              </TabsContent>
            </Tabs>
          )}
        </DialogContent>
      </Dialog>


      <InvoiceSettingsDialog open={setOpen} onClose={() => setSetOpen(false)} />

      {/* Invoice numbering */}
      <Dialog open={numOpen} onOpenChange={(o) => !o && setNumOpen(false)}>
        <DialogContent className="rounded-none max-w-md" data-testid="numbering-dialog">
          <DialogHeader><DialogTitle className="font-heading uppercase tracking-tight flex items-center gap-2"><Hash className="h-5 w-5 text-primary" /> Invoice Numbering</DialogTitle></DialogHeader>
          {numInfo ? (
            <div className="space-y-3 py-2 text-sm">
              <div className="border border-border p-3 grid grid-cols-3 gap-2">
                <div><div className="text-[11px] uppercase text-muted-foreground">Financial Year</div><div className="font-bold">{numInfo.fy}</div></div>
                <div><div className="text-[11px] uppercase text-muted-foreground">Issued</div><div className="font-mono-num font-bold">{numInfo.issued}</div></div>
                <div><div className="text-[11px] uppercase text-muted-foreground">Next #</div><div className="font-mono-num font-bold">{String(numInfo.next_number).padStart(4, "0")}</div></div>
              </div>
              <div><Label>Start next invoice at number</Label>
                <Input type="number" min="1" className="rounded-none mt-1 font-mono-num" value={numStart} onChange={(e) => setNumStart(e.target.value)} data-testid="numbering-input" />
                <p className="text-[11px] text-muted-foreground mt-1">Next invoice will be <span className="font-mono-num">AIPM/{numInfo.fy}/{parseInt(numStart, 10) || 0}</span>. Must be greater than the {numInfo.issued} already issued this year.</p>
              </div>
            </div>
          ) : <div className="py-6 text-center text-sm text-muted-foreground">Loading…</div>}
          <DialogFooter>
            <Button variant="ghost" className="rounded-none" onClick={() => setNumOpen(false)}>Cancel</Button>
            <Button className="rounded-none font-bold uppercase tracking-wide" onClick={saveNumbering} data-testid="numbering-save">Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Record invoice payment */}
      <Dialog open={!!payFor} onOpenChange={(o) => !o && setPayFor(null)}>
        <DialogContent className="rounded-none max-w-md" data-testid="inv-payment-dialog">
          <DialogHeader><DialogTitle className="font-heading uppercase tracking-tight">Record Payment</DialogTitle></DialogHeader>
          {payFor && (
            <div className="space-y-3 py-2">
              <div className="text-sm text-muted-foreground">Invoice <span className="font-mono-num text-primary">{payFor.serial}</span></div>
              <div className="border border-border p-3 grid grid-cols-3 gap-2 text-sm">
                <div><div className="text-[11px] uppercase text-muted-foreground">Total</div><div className="font-mono-num font-bold">{money(payFor.total)}</div></div>
                <div><div className="text-[11px] uppercase text-muted-foreground">Paid</div><div className="font-mono-num font-bold text-emerald-500">{money(payFor.paid)}</div></div>
                <div><div className="text-[11px] uppercase text-muted-foreground">Pending</div><div className="font-mono-num font-bold text-primary">{money((payFor.total || 0) - (payFor.paid || 0))}</div></div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div><Label>Amount (₹)</Label><Input type="number" min="1" step="0.01" className="rounded-none mt-1 font-mono-num" value={payAmount} onChange={(e) => setPayAmount(e.target.value)} data-testid="inv-pay-amount" autoFocus /></div>
                <div><Label>Date</Label><Input type="date" className="rounded-none mt-1" value={payDate} onChange={(e) => setPayDate(e.target.value)} /></div>
              </div>
              <div><Label>Mode</Label>
                <select className="rounded-none mt-1 h-10 w-full bg-background border border-border px-2 text-sm" value={payMode} onChange={(e) => setPayMode(e.target.value)} data-testid="inv-pay-mode">
                  {["Bank Transfer", "UPI", "Cash", "Cheque", "Credit / Debit Card", "Other"].map((m) => <option key={m} value={m}>{m}</option>)}
                </select>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="ghost" className="rounded-none" onClick={() => setPayFor(null)}>Cancel</Button>
            <Button className="rounded-none font-bold uppercase tracking-wide" onClick={submitPay} data-testid="inv-pay-save">Record Payment</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
